import { IconNames } from '@icon/assets';
import { IntradayChartPoint } from '../../../models/intraday-chart.model';
import { Symbol, symbolIcon } from '../../../models/symbol.model';

export interface MarketSymbol {
	id: string;
	/** Numeric contract id used to match SSE price ticks (`MarketViewMessage.contractId`) */
	contractId: number;
	title: string;
	subtitle: string;
	price: number;
	changePercent: number;
	pinned: boolean;
	/** Commodity icon shown next to the title — see `symbolIcon`. */
	icon: IconNames;
	/** Yesterday's closing price — sparkline baseline */
	baseline: number;
	/** Current-session trades for the sparkline */
	sparklineTrades: IntradayChartPoint[];
}

/** Percent move of the last trade against the previous settlement, rounded to 2 decimals. */
export function changePercent(lastTradedPrice: number, lastSettlementPrice: number): number {
	if (!lastSettlementPrice || !lastTradedPrice) {
		return 0;
	}

	return Math.round(((lastTradedPrice - lastSettlementPrice) / lastSettlementPrice) * 10000) / 100;
}

/**
 * The backend's own change as a percent of the previous settlement. Used for the snapshot
 * rather than {@link changePercent} because the backend zeroes the change from midnight to
 * the next open, while the snapshot's last price is still yesterday's.
 */
export function changeToPercent(change: number, lastSettlementPrice: number): number {
	if (!lastSettlementPrice || !change) {
		return 0;
	}

	return Math.round((change / lastSettlementPrice) * 10000) / 100;
}

export function mapSymbolToMarketSymbol(symbol: Symbol, pinned = false): MarketSymbol {
	return {
		id: symbol.isin,
		contractId: symbol.contractId,
		title: symbol.symbolShortName,
		subtitle: symbol.symbolName,
		price: symbol.lastTradedPrice,
		changePercent: changeToPercent(symbol.lastPriceChange, symbol.lastSettlementPrice),
		pinned,
		icon: symbolIcon(symbol.isin),
		baseline: symbol.lastSettlementPrice,
		sparklineTrades: []
	};
}

/**
 * Stamps each row with the trader's pin state and lifts the pinned ones to the
 * top of the list.
 *
 * Both groups keep the order the API sent — by traded value, descending — so a
 * pin lifts one card without reshuffling the rest, and a symbol that appears in
 * the market for the first time simply lands unpinned wherever the API put it.
 * Rows whose pin state is unchanged keep their identity, so a re-render caused
 * by a price tick does not rebuild every card.
 *
 * @param pinnedIsins the trader's pinned `MarketSymbol.id`s, as the stored
 * setting has them. Entries for symbols the market no longer lists are ignored.
 */
export function withPinsApplied(items: readonly MarketSymbol[], pinnedIsins: readonly string[]): MarketSymbol[] {
	const pinned = new Set(pinnedIsins);

	const flagged = items.map(item => {
		const isPinned = pinned.has(item.id);
		return item.pinned === isPinned ? item : { ...item, pinned: isPinned };
	});

	return [...flagged.filter(item => item.pinned), ...flagged.filter(item => !item.pinned)];
}
